import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/** The one state a stored mail is in (os#909), as `os.mail_list` filters by it. */
const HANDLED_STATES = ['unhandled', 'replied', 'skipped', 'snoozed', 'escalated'] as const

/**
 * The states a person's own mail can be put in (#885): a member's `escalated`, raised to the enterprise owner, has no
 * meaning for a mailbox nobody but its holder reads, so it is not offered and the API refuses it.
 */
export const PERSON_HANDLED_STATES = ['unhandled', 'replied', 'skipped', 'snoozed'] as const

const TRIAGE_FIELDS =
    '`to`, `cc`, `bcc` (who a mail the mailbox sent went to unseen, `[]` on everything else), `threadId`, `labels` (free text, `[]` when none), `handledState` (`unhandled`, `replied`, `skipped`, `snoozed` or `escalated`) and `snoozedUntil` (only when `snoozed`)'

const NOT_A_PERSON: BuiltInToolError = {
    code: 'api_token_cannot_reach_mail',
    when: 'An API token called it. A token belongs to the enterprise and to nobody, so it has no mailbox.',
    handling: 'tool_error',
}

const NO_MEMBERSHIP: BuiltInToolError = {
    code: 'user_not_found',
    when: 'The caller is no longer a member of the enterprise the call is in.',
    handling: 'tool_error',
}

const NOT_IN_MAILBOX: BuiltInToolError = {
    code: 'email_not_found',
    when: "The id is not in the caller's own mailbox, including a member's mail and another person's.",
    handling: 'tool_error',
}

/** What one contact reads as, in every contacts tool's result (#1114). */
export const CONTACT_FIELDS =
    '`id` (null on a directory row), `address`, `name` (empty when none is known), `source` (`auto` when a send saved it, `manual` when it was added by hand, `directory` for one of the enterprise’s own people or members offered beside the contacts), `nameByHand` (a name set by hand, which no later send replaces), `mailCount` and `lastMailedAt` (null until the mailbox first writes to it)'

/**
 * The body every send, reply and forward takes written in Markdown (#1117), the way a model writes: the OS renders it to
 * a formatted HTML part, sanitised, and derives the plain-text part from that, so a skill never has to ask for HTML.
 */
export const MARKDOWN_BODY = {
    type: 'string',
    description:
        'The body in Markdown: headings, emphasis, lists, links, quotes, code and tables. The OS sends it formatted, with a plain-text copy. Give this or text, not both',
} as const

/** What each send tool's description says about its body (#1117): Markdown first, `text` and `html` as before. */
export const MARKDOWN_BODY_SENTENCE =
    'Write the body as `markdown`; the OS sends it formatted with a plain-text copy, and raw HTML in it is not passed through. Or pass text, with optional html for a body you formatted yourself, as before; give markdown or text, not both, and never markdown with html.'

/**
 * The refusals of a body given both ways, or not at all (#1117). A member's send is handed back before its gate opens;
 * a person's is refused by the API before anything leaves.
 */
export function mailBodyErrors(handling: BuiltInToolError['handling'], required = true): BuiltInToolError[] {
    const hint =
        'Send the body once: as markdown (preferred, the OS writes the plain-text copy), or as text with optional html.'
    return [
        ...(required
            ? [
                  {
                      code: 'mail_field_required',
                      when: 'Neither markdown nor text says anything.',
                      handling,
                      hint,
                  },
              ]
            : []),
        {
            code: 'mail_body_conflict',
            when: 'markdown was given with text or with html.',
            handling,
            hint,
        },
    ]
}

/** The most addresses one mail goes to, across to, cc and bcc (#1119); the API refuses more. */
export const MAIL_RECIPIENTS_MAX = 50

/** A recipient field (#1119): one address, or a list of them. */
export function recipientsField(description: string): JsonSchema {
    return { description, anyOf: [{ type: 'string' }, { type: 'array', items: { type: 'string' } }] }
}

/** What every send and forward says about its recipients (#1119). */
export const RECIPIENTS_SENTENCE = `to, cc and bcc each take one address or a list of them. Everyone in to and cc sees who else the mail went to; nobody sees bcc. An address named twice gets one copy, and a mail goes to at most ${MAIL_RECIPIENTS_MAX} addresses in all.`

/** The refusals of a mail's recipients (#1119), on a send and a forward. */
export function recipientErrors(handling: BuiltInToolError['handling']): BuiltInToolError[] {
    return [
        {
            code: 'recipient_invalid',
            when: 'An address in to, cc or bcc is not an email address, or to names nobody. The error names the address.',
            handling,
        },
        {
            code: 'too_many_recipients',
            when: `to, cc and bcc name more than ${MAIL_RECIPIENTS_MAX} addresses in all.`,
            handling,
        },
    ]
}

/** What a reply says about who it goes to (#1119): its sender, everyone on the original with replyAll, and any cc and bcc. */
export const REPLY_RECIPIENTS_SENTENCE = `It goes to the sender of the message being answered. Pass replyAll true to answer everyone on the original too: everyone else on its To and Cc is copied in, but for this mailbox’s own addresses. Optional cc and bcc, each one address or a list, copy in more people, seen or unseen; ${MAIL_RECIPIENTS_MAX} addresses at most in all.`

/** A reply's recipient fields (#1119). */
export const REPLY_RECIPIENT_FIELDS = {
    replyAll: {
        type: 'boolean',
        description:
            'true answers everyone on the original: its sender, and everyone else on its To and Cc but this mailbox',
    },
    cc: recipientsField('More people to copy in, seen by every recipient: one address or a list'),
    bcc: recipientsField('People sent the reply that no other recipient sees: one address or a list'),
} as const satisfies Record<string, JsonSchema>

/**
 * A person's reply naming who it goes to in place of the original's sender. Only a person's own reply takes it: a
 * member's run always answers the original's sender (`os.mail_reply` has no `to`).
 */
export const REPLY_TO_FIELD = recipientsField(
    'Who the reply goes to, in place of the sender of the message being answered: one address or a list. Left out, it goes to that sender',
)

/** What a person's reply says about naming its own `to`. */
export const REPLY_TO_SENTENCE =
    'Pass to, one address or a list, to send it to them in place of that sender; the subject, the address it leaves from and the threading still come from the original. With replyAll, anyone already in to is not copied in again.'

/**
 * A reply's refusals over who it goes to (#1119). `to` says the reply takes its own `to` (a person's reply only), so the
 * refusal names it among the fields an address may be wrong in.
 */
export function replyRecipientErrors(handling: BuiltInToolError['handling'], to = false): BuiltInToolError[] {
    return [
        {
            code: 'recipient_invalid',
            when: `The stored message names no address that can be replied to, or an address in ${to ? 'to, cc or bcc' : 'cc or bcc'} is not an email address. The error names the address.`,
            handling,
        },
        {
            code: 'too_many_recipients',
            when: `The reply would go to more than ${MAIL_RECIPIENTS_MAX} addresses in all, everyone a reply-all copies in counted.`,
            handling,
        },
    ]
}

/** The opt-out every send tool takes (#1115): the OS adds the person's signature unless the call says not to. */
const SIGNATURE_FLAG = {
    type: 'boolean',
    description: 'false sends without your signature. Left out, the OS adds it when you have one',
} as const

/** The reply's opt-out (#1116): the OS quotes the message being answered below the reply unless the call says not to. */
const QUOTE_FLAG = {
    type: 'boolean',
    description:
        'false sends without the original quoted below. Left out, the OS quotes the message you are answering after the body and your signature',
} as const

/** What a person's signature reads as (#1115), in the signature tools' results. */
const SIGNATURE_FIELDS =
    '`html` (sanitised, empty for a signature written as text), `text` (its plain-text form), `updatedAt` (null when you have none), `useTemplate` (whether the enterprise’s signature template signs your mail while you have no signature of your own) and `template` (that template filled in for you, `{ html, text }`, or null when the enterprise has none)'

const SIGNATURE_TOO_LARGE: BuiltInToolError = {
    code: 'signature_too_large',
    when: 'html or text is larger than 20 KB.',
    handling: 'tool_error',
}

const SIGNATURE_REFUSALS: readonly BuiltInToolError[] = [
    {
        code: 'signature_invalid',
        when: 'html or text is not text, or useTemplate is not true or false.',
        handling: 'tool_error',
    },
    SIGNATURE_TOO_LARGE,
]

/** What one of a person's extra addresses reads as (#1122), in the address tools' results. */
const ADDRESS_FIELDS =
    '`id`, `address`, `replyFromAlias` (whether a reply to mail that arrived at it leaves from it, rather than from your own address) and `signature` (its own, `{ html, text }` with `html` sanitised and empty for one written as text, or null while it signs as your mailbox does)'

/** The refusals of one extra address's signature (#1122): the address, then the signature by a mailbox's rules. */
const ADDRESS_SIGNATURE_REFUSALS: readonly BuiltInToolError[] = [
    {
        code: 'alias_not_found',
        when: 'The id is not one of your extra addresses, including another mailbox’s.',
        handling: 'tool_error',
    },
    {
        code: 'use_mailbox_owner_handoff',
        when: 'The address has since become a member’s own address, so it is no longer one of yours to sign.',
        handling: 'tool_error',
    },
    {
        code: 'alias_address_taken',
        when: 'The address has since become a person’s own address, so it is no longer one of yours to sign.',
        handling: 'tool_error',
    },
    { code: 'signature_invalid', when: 'html or text is not text.', handling: 'tool_error' },
    SIGNATURE_TOO_LARGE,
]

const CONTACT_NOT_FOUND: BuiltInToolError = {
    code: 'contact_not_found',
    when: 'The id is not one of your contacts, including another mailbox’s.',
    handling: 'tool_error',
}

/**
 * A person's own mailbox (#883), for the person's own agent over the API and the OS MCP server: what `os.mail` is for
 * a run, read as the person instead of as a member. The mailbox is always the caller's: the person who signed in, or
 * who approved the connection making the call, in the enterprise the session or connection is in. There is no id
 * to name anyone else's, so nobody reads a mailbox but its holder: not an owner, not a member's run, and not an API
 * token, which is nobody and is refused the `mail` scope at issuance. A run never calls these, so a task that
 * declares one is refused when it is saved (`tool_not_run_callable`). Beside reading, filing, replying and sending,
 * the person forwards and deletes a mail, and presses the one-click buttons their inbox offers: the tasks that declare a
 * mailbox action, started over the person's own mailbox.
 */
export const OS_MY_MAIL_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.my_mail_list',
        family: 'os.my_mail',
        summary: 'List the mail in your own mailbox, newest first: metadata and a snippet, no bodies.',
        description:
            'List the mail in your own mailbox, newest first: the mailbox of the person who signed in or connected this app, in the enterprise this call is in, and never a member’s or anyone else’s. Metadata and a snippet only, no bodies: read one with os.my_mail_read. Optional unreadOnly (only messages not yet opened), label (only mail carrying that label), handledState (only mail in that state), threadId (every mail in one conversation), search (only mail whose sender address or name, subject or text contains the words, any case), order (desc, the default, or asc), page and limit (default 25, max 200). Filters combine: label VIP with handledState unhandled lists only unhandled VIP mail; search invoice with label Acme finds the Acme mail about an invoice. A full row is large: about 30 fit in one result. To work through a long backlog pass compact true, which returns only id, fromAddress, subject, receivedAt, labels, handledState, spamScore and spamFlag, so about 100 rows fit.',
        inputSchema: {
            type: 'object',
            properties: {
                unreadOnly: { type: 'boolean', description: 'Only messages nobody has opened yet' },
                label: { type: 'string', description: 'Only mail carrying this label, e.g. VIP' },
                handledState: {
                    type: 'string',
                    enum: HANDLED_STATES,
                    description: 'Only mail in this handled state',
                },
                threadId: { type: 'string', description: 'Only mail in this conversation, from a row’s threadId' },
                search: {
                    type: 'string',
                    description: 'Only mail whose sender address or name, subject or text contains this, any case',
                },
                order: { type: 'string', enum: ['desc', 'asc'], description: 'By receivedAt. Default desc' },
                page: { type: 'integer', minimum: 1, description: 'The page of results, from 1' },
                limit: { type: 'integer', minimum: 1, maximum: 200, description: 'Rows per page. Default 25' },
                compact: {
                    type: 'boolean',
                    description:
                        'Only id, fromAddress, subject, receivedAt, labels, handledState, spamScore and spamFlag on each row',
                },
            },
            additionalProperties: false,
        },
        returns: `\`{ data, total, page, limit, pages }\`. Each row has \`id\`, \`fromAddress\`, \`fromName\`, \`subject\`, \`snippet\`, \`receivedAt\`, \`readAt\`, \`hasAttachments\`, \`threadKey\` (the same on every message in one conversation), \`direction\` (\`inbound\` when it arrived, \`outbound\` when you sent it), ${TRIAGE_FIELDS}, \`spamScore\` and \`spamFlag\` (null if the receiving edge has not scored the message). With \`compact\`, each row has only \`id\`, \`fromAddress\`, \`subject\`, \`receivedAt\`, \`labels\`, \`handledState\`, \`spamScore\` and \`spamFlag\`.`,
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            {
                code: 'handled_state_invalid',
                when: 'handledState is not one of the handled states.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'GET', path: '/v1/mail', scope: 'mail:read' },
        examples: [
            { input: { unreadOnly: true, limit: 10 } },
            { input: { handledState: 'unhandled', order: 'asc', compact: true, limit: 100 } },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_read',
        family: 'os.my_mail',
        summary: 'Read one message from your own mailbox.',
        description:
            'Read one message from your own mailbox: sender, subject, text and sanitised HTML bodies, and attachment names and sizes (fetch one with os.my_mail_attachment). Reading marks it read for you too. Pass id from os.my_mail_list; an id from anyone else’s mailbox is not found.',
        inputSchema: {
            type: 'object',
            properties: { id: { type: 'string', description: 'A message id from os.my_mail_list' } },
            required: ['id'],
            additionalProperties: false,
        },
        returns: `The message: sender, subject, \`textBody\`, sanitised \`htmlBody\`, \`receivedAt\`, \`readAt\`, \`attachments\` (each with \`id\`, \`filename\`, \`contentType\` and \`sizeBytes\`, not contents), ${TRIAGE_FIELDS}, \`spamScore\` and \`spamFlag\` (null if the receiving edge has not scored the message).`,
        callers: ['api', 'mcp'],
        effect: 'read',
        sideEffects: 'Marks the message read for you, the same as opening it.',
        retry: 'safe',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP, NOT_IN_MAILBOX],
        api: { method: 'GET', path: '/v1/mail/:id', scope: 'mail:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000003' } }],
        public: true,
    },
    {
        name: 'os.my_mail_attachment',
        family: 'os.my_mail',
        summary: 'Fetch one attachment of a message in your own mailbox, base64-encoded.',
        description:
            'Fetch one attachment of a message in your own mailbox, as its file name, content type, size and base64-encoded bytes. Pass id, a message id from os.my_mail_list, and attachmentId from that message’s attachments in os.my_mail_read. A file over 4 MB is refused here: download it from the API instead (GET /v1/mail/{id}/attachments/{attachmentId} without format=json streams the bytes).',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.my_mail_list' },
                attachmentId: { type: 'string', description: 'An attachment id from os.my_mail_read' },
            },
            required: ['id', 'attachmentId'],
            additionalProperties: false,
        },
        returns: '`{ filename, contentType, sizeBytes, base64 }`: the file, encoded.',
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            NOT_IN_MAILBOX,
            {
                code: 'attachment_not_found',
                when: 'The message has no attachment with that id.',
                handling: 'tool_error',
            },
            {
                code: 'attachment_too_large',
                when: 'The file is over 4 MB, more than a tool result carries. Download it from the API instead.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'GET', path: '/v1/mail/:id/attachments/:attachmentId?format=json', scope: 'mail:read' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000003',
                    attachmentId: '5f0c1d2e-0000-4000-8000-000000000009',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_set_label',
        family: 'os.my_mail',
        summary: 'Set the labels on a message in your own mailbox.',
        description:
            'Set the labels on one message in your own mailbox. Pass id from os.my_mail_list and labels, the whole set the mail should carry: it replaces whatever labels it had, and an empty list clears them. Labels are free text (e.g. VIP, Needs reply); there is no fixed list to choose from. List mail by label with os.my_mail_list.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.my_mail_list' },
                labels: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Every label the mail should carry. Replaces the current set',
                },
            },
            required: ['id', 'labels'],
            additionalProperties: false,
        },
        returns: '`{ id, labels }`: the mail and the labels it now carries.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            NOT_IN_MAILBOX,
            {
                code: 'labels_invalid',
                when: 'labels is not a list of non-empty strings of at most 64 characters.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'PATCH', path: '/v1/mail/:id/labels', scope: 'mail:write' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000003', labels: ['VIP'] } }],
        public: true,
    },
    {
        name: 'os.my_mail_set_handled',
        family: 'os.my_mail',
        summary:
            'Record where a message in your own mailbox stands: unhandled, replied, skipped, or snoozed until a date.',
        description:
            'Record where one message in your own mailbox stands. Pass id from os.my_mail_list and handledState: unhandled, replied, skipped or snoozed. A mail is in one state at a time. snoozed needs snoozedUntil, the date and time it comes back (ISO 8601); every other state takes no date and clears any snooze. escalated is a member’s state, raised to the enterprise owner: your mail has nobody to raise to, so it is refused. os.my_mail_reply moves the mail it answers to replied on its own.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.my_mail_list' },
                handledState: {
                    type: 'string',
                    enum: PERSON_HANDLED_STATES,
                    description: 'Where the mail stands now',
                },
                snoozedUntil: {
                    type: 'string',
                    description: 'ISO 8601 date and time the mail comes back. Only with handledState snoozed',
                },
            },
            required: ['id', 'handledState'],
            additionalProperties: false,
        },
        returns: '`{ id, handledState, snoozedUntil }`: the state the mail is now in.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            NOT_IN_MAILBOX,
            {
                code: 'handled_state_invalid',
                when: 'handledState is not unhandled, replied, skipped or snoozed (escalated is a member’s state).',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_required',
                when: 'handledState is snoozed and snoozedUntil is missing or not a date.',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_not_allowed',
                when: 'snoozedUntil was given with a handledState other than snoozed.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'PATCH', path: '/v1/mail/:id/handled', scope: 'mail:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000003',
                    handledState: 'snoozed',
                    snoozedUntil: '2026-11-03T09:00:00Z',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_set_many',
        family: 'os.my_mail',
        summary: 'Set the labels, the handled state, or both on many messages in your own mailbox at once.',
        description:
            'Record the same decision on many messages in your own mailbox in one call. Name the mail one way: ids (up to 200 message ids from os.my_mail_list), or fromAddress (every unhandled mail that arrived from that exact sender address; mail already handled and mail you sent are never touched). Then pass labels (the whole set each mail should carry, replacing what it had), handledState (unhandled, replied, skipped or snoozed; snoozed needs snoozedUntil), or both. Use it to close a run of look-alike mail without reading each one. An id that is not in your mailbox changes nothing and comes back in notFound.',
        inputSchema: {
            type: 'object',
            properties: {
                ids: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Up to 200 message ids from os.my_mail_list. Not together with fromAddress',
                },
                fromAddress: {
                    type: 'string',
                    description:
                        'A sender address, matched exactly: every unhandled mail that arrived from it. Not together with ids',
                },
                labels: {
                    type: 'array',
                    items: { type: 'string' },
                    description: 'Every label each mail should carry. Replaces the current set',
                },
                handledState: {
                    type: 'string',
                    enum: PERSON_HANDLED_STATES,
                    description: 'Where each mail stands now',
                },
                snoozedUntil: {
                    type: 'string',
                    description: 'ISO 8601 date and time the mail comes back. Only with handledState snoozed',
                },
            },
            additionalProperties: false,
        },
        returns:
            '`{ updated, notFound }`: how many mails were changed, and the ids given that are not in your mailbox (always `[]` when selecting by `fromAddress`).',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            {
                code: 'mail_selector_invalid',
                when: 'Neither ids nor fromAddress was given, or both were.',
                handling: 'tool_error',
            },
            {
                code: 'mail_ids_invalid',
                when: 'ids is not a list of 1 to 200 non-empty message ids.',
                handling: 'tool_error',
            },
            {
                code: 'mail_change_required',
                when: 'Neither labels nor handledState was given, so there is nothing to record.',
                handling: 'tool_error',
            },
            {
                code: 'labels_invalid',
                when: 'labels is not a list of non-empty strings of at most 64 characters.',
                handling: 'tool_error',
            },
            {
                code: 'handled_state_invalid',
                when: 'handledState is not unhandled, replied, skipped or snoozed (escalated is a member’s state).',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_required',
                when: 'handledState is snoozed and snoozedUntil is missing or not a date.',
                handling: 'tool_error',
            },
            {
                code: 'snoozed_until_not_allowed',
                when: 'snoozedUntil was given with a handledState other than snoozed.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/mail/triage', scope: 'mail:write' },
        examples: [
            { input: { fromAddress: 'updates@example.com', labels: ['Newsletter'], handledState: 'skipped' } },
            {
                input: {
                    ids: ['5f0c1d2e-0000-4000-8000-000000000003', '5f0c1d2e-0000-4000-8000-000000000004'],
                    handledState: 'skipped',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_reply',
        family: 'os.my_mail',
        summary: 'Reply to a message in your own mailbox, from your own address, with no approval step.',
        description:
            'Reply to one message in your own mailbox. Pass id from os.my_mail_list and the reply. ' +
            MARKDOWN_BODY_SENTENCE +
            ' The subject comes from the message being answered, and the reply goes out from the address it arrived at, which has to be yours. ' +
            REPLY_RECIPIENTS_SENTENCE +
            ' ' +
            REPLY_TO_SENTENCE +
            ' It threads under the original in the recipient’s mail client. Your signature, when you have set one, is added after the body; pass signature false to leave it off. The message you are answering is quoted below your reply and signature, under an `On <date>, <name> wrote:` line, as `>` lines in text and a blockquote in html; pass quote false to leave it off. Your own mail is yours: nothing waits for anyone’s approval, and the message leaves in this call. The sent copy is filed in your mailbox in the original’s thread, and the original is marked read and moves to handled state replied.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.my_mail_list: the mail this answers' },
                markdown: MARKDOWN_BODY,
                text: { type: 'string', description: 'The reply, as plain text, when not given as markdown' },
                html: { type: 'string', description: 'Optional HTML body beside text. Plain text is sent either way' },
                to: REPLY_TO_FIELD,
                ...REPLY_RECIPIENT_FIELDS,
                signature: SIGNATURE_FLAG,
                quote: QUOTE_FLAG,
            },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            '`{ status, id }`: `sent`, or `send_failed` with the provider’s `error` when it did not go out. `id` is the copy filed in your mailbox either way.',
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'Files the sent reply in your mailbox, in the original’s thread, marks the original read and, once sent, moves it to handled state `replied` (clearing any snooze) and saves every recipient to your contacts.',
        retry: 'unsafe',
        retryNote: 'A repeat sends a second reply. Call it again only if the first reported a refusal.',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            ...mailBodyErrors('tool_error'),
            NOT_IN_MAILBOX,
            {
                code: 'no_mail_address',
                when: 'You have no address in this enterprise yet, so nothing can leave as you. An owner gives one on People.',
                handling: 'tool_error',
            },
            ...replyRecipientErrors('tool_error', true),
            {
                code: 'sender_not_own_mailbox',
                when: 'The address the original arrived at is not yours, or its domain is not active for sending.',
                handling: 'tool_error',
            },
            {
                code: 'sending_unavailable',
                when: 'Sending from the enterprise’s own domain is not configured on this deployment.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/mail/:id/reply', scope: 'mail:write' },
        examples: [
            { input: { id: '5f0c1d2e-0000-4000-8000-000000000003', markdown: 'Thursday at **10:00** works.' } },
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000003',
                    replyAll: true,
                    markdown: 'Thursday at **10:00** works for all of us.',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_send',
        family: 'os.my_mail',
        summary: 'Send a new mail from your own address, with no approval step.',
        description:
            'Send a new message from your own mailbox: to, subject and the body. ' +
            RECIPIENTS_SENTENCE +
            ' ' +
            MARKDOWN_BODY_SENTENCE +
            ' It goes out from your own address in this enterprise, the one an owner gave you, or from one of your extra addresses when you name it as `from`. Your signature, when you have set one, is added after the body; pass signature false to leave it off. Your own mail is yours: nothing waits for anyone’s approval, and the message leaves in this call. The sent copy is filed in your mailbox. To answer a message already in your mailbox, use os.my_mail_reply instead, so it threads.',
        inputSchema: {
            type: 'object',
            properties: {
                to: recipientsField('Who the mail is to: one email address, or a list'),
                cc: recipientsField('Who is copied in, seen by every recipient: one address or a list'),
                bcc: recipientsField('Who is sent the mail unseen by any other recipient: one address or a list'),
                subject: { type: 'string', description: 'The subject line' },
                markdown: MARKDOWN_BODY,
                text: { type: 'string', description: 'The message, as plain text, when not given as markdown' },
                html: { type: 'string', description: 'Optional HTML body beside text. Plain text is sent either way' },
                from: {
                    type: 'string',
                    description:
                        'Optional sender: one of your extra addresses in this enterprise. Left out, your own address is used',
                },
                signature: SIGNATURE_FLAG,
            },
            required: ['to', 'subject'],
            additionalProperties: false,
        },
        returns:
            '`{ status, id }`: `sent`, or `send_failed` with the provider’s `error` when it did not go out. `id` is the copy filed in your mailbox either way.',
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'Files the sent message in your mailbox, every recipient on it, and, once sent, saves every recipient to your contacts.',
        retry: 'unsafe',
        retryNote: 'A repeat sends a second message. Call it again only if the first reported a refusal.',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            ...mailBodyErrors('tool_error'),
            {
                code: 'no_mail_address',
                when: 'You have no address in this enterprise yet, so nothing can leave as you. An owner gives one on People.',
                handling: 'tool_error',
            },
            ...recipientErrors('tool_error'),
            {
                code: 'sender_not_own_mailbox',
                when: 'Your address’s domain is not active for sending, or `from` is not an address you hold.',
                handling: 'tool_error',
            },
            {
                code: 'sending_unavailable',
                when: 'Sending from the enterprise’s own domain is not configured on this deployment.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/mail', scope: 'mail:write' },
        examples: [
            {
                input: {
                    to: 'vip@acme.com',
                    subject: 'Thursday',
                    markdown: 'Thursday at **10:00** works for me.',
                },
            },
            {
                input: {
                    to: ['vip@acme.com', 'ops@acme.com'],
                    cc: 'assistant@acme.com',
                    subject: 'Thursday',
                    markdown: 'Thursday at **10:00** works for me.',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_forward',
        family: 'os.my_mail',
        summary:
            'Forward a message from your own mailbox to somebody else, from your own address, with no approval step.',
        description:
            'Forward one message from your own mailbox to somebody else. Pass id from os.my_mail_list and to, who it goes to. ' +
            RECIPIENTS_SENTENCE +
            ' Optional markdown or text (not both) is a note placed above the forwarded message, sent formatted with a plain-text copy when written as markdown, and optional from is one of your extra addresses to send from. The new message carries the original under a forwarded-message line that says who wrote it and when: its text and, when it has HTML, its formatting, sanitised; the original’s attachments are not forwarded (fetch one with os.my_mail_attachment and send it another way). Your signature, when you have set one, is added after your note and above the forwarded message; pass signature false to leave it off. Your own mail is yours: nothing waits for anyone’s approval, and the message leaves in this call. The sent copy is filed in your mailbox in the original’s thread, and the original is marked read; its handled state does not change.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'A message id from os.my_mail_list: the mail to pass on' },
                to: recipientsField('Who the forward is to: one email address, or a list'),
                cc: recipientsField('Who is copied in, seen by every recipient: one address or a list'),
                bcc: recipientsField('Who is sent the forward unseen by any other recipient: one address or a list'),
                markdown: {
                    type: 'string',
                    description:
                        'Optional note above the forwarded message, in Markdown. The OS sends it formatted, with a plain-text copy. Give this or text, not both',
                },
                text: { type: 'string', description: 'Optional note above the forwarded message, as plain text' },
                from: {
                    type: 'string',
                    description:
                        'Optional sender: one of your extra addresses in this enterprise. Left out, your own address is used',
                },
                signature: SIGNATURE_FLAG,
            },
            required: ['id', 'to'],
            additionalProperties: false,
        },
        returns:
            '`{ status, id }`: `sent`, or `send_failed` with the provider’s `error` when it did not go out. `id` is the copy filed in your mailbox either way.',
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'Files the forwarded message in your mailbox, in the original’s thread, marks the original read and, once sent, saves every recipient to your contacts.',
        retry: 'unsafe',
        retryNote: 'A repeat forwards the message a second time. Call it again only if the first reported a refusal.',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            ...mailBodyErrors('tool_error', false),
            NOT_IN_MAILBOX,
            {
                code: 'no_mail_address',
                when: 'You have no address in this enterprise yet, so nothing can leave as you. An owner gives one on People.',
                handling: 'tool_error',
            },
            ...recipientErrors('tool_error'),
            {
                code: 'sender_not_own_mailbox',
                when: 'Your address’s domain is not active for sending, or `from` is not an address you hold.',
                handling: 'tool_error',
            },
            {
                code: 'sending_unavailable',
                when: 'Sending from the enterprise’s own domain is not configured on this deployment.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/mail/:id/forward', scope: 'mail:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000003',
                    to: 'colleague@acme.com',
                    text: 'Can you take this one?',
                },
            },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_delete',
        family: 'os.my_mail',
        summary: 'Delete a message from your own mailbox for good.',
        description:
            'Delete one message from your own mailbox for good: the message, its attachments and their stored files are removed, and nothing brings them back. Pass id from os.my_mail_list. This is not the archive: to keep a mail out of the way, record it as skipped with os.my_mail_set_handled instead. An id from anyone else’s mailbox is not found.',
        inputSchema: {
            type: 'object',
            properties: { id: { type: 'string', description: 'A message id from os.my_mail_list' } },
            required: ['id'],
            additionalProperties: false,
        },
        returns: '`{ id }`: the message that is gone.',
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'Removes the message, its attachment records and their stored files; their storage is no longer counted.',
        retry: 'idempotent',
        retryNote: 'A repeat finds no such message and reports email_not_found: the message is gone either way.',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            NOT_IN_MAILBOX,
            {
                code: 'mail_delete_failed',
                when: 'The message’s stored files could not be removed, so the message was kept whole. Try again.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'DELETE', path: '/v1/mail/:id', scope: 'mail:write' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000003' } }],
        public: true,
    },
    {
        name: 'os.my_mail_actions',
        family: 'os.my_mail',
        summary: 'List the one-click buttons your inbox offers: the tasks that declare a mailbox action.',
        description:
            'List the one-click buttons your inbox offers. Each is a task whose current version declares a mailbox action: a task of the enterprise, which os.my_mail_start_action starts over your own mailbox (taskId set), or a public Catalog template that declares one and the enterprise holds no clone of, which has to be cloned first (catalogTaskId set). on says where the button sits: mailbox, over the whole mailbox, or mail, on one message, which starts the run with that message’s id. label is the button’s words, as the task declares them. schedule is your own cadence for a button, set with os.my_mail_schedule_action, or null.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            '`{ actions }`: each `{ taskId, catalogTaskId, slug, name, label, on, schedule }`, exactly one of `taskId` and `catalogTaskId` set, by label. `schedule` is your own cadence for that button (`{ cron, timezone, enabled, nextRunAt, lastFiredAt, lastRunId, refusalReason }`), or null when you press it only by hand.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP],
        api: { method: 'GET', path: '/v1/mail/actions', scope: 'mail:read' },
        examples: [{ input: {} }],
        public: true,
        chatPrompt:
            'os.my_mail_actions lists the buttons on the inbox of the person writing to you: their own mailbox, which is theirs and never a member’s. When they ask for one of their mail tasks to run on their inbox, by hand or on a cadence, use these tools rather than starting, copying or changing a task, and never move their address onto a member.',
    },
    {
        name: 'os.my_mail_start_action',
        family: 'os.my_mail',
        summary: 'Press one of your inbox’s buttons: start that task over your own mailbox.',
        description:
            'Press one of the buttons os.my_mail_actions lists: start the task by its taskId, over your own mailbox. The run reads and files your mail rather than its member’s, and the runs it chains do too. For a button on one message (on mail), pass emailId, the message it is pressed on, which the run receives as its emailId input. A busy task or member makes the run wait its turn as pending rather than refusing. A template (catalogTaskId set) cannot be started until it is cloned.',
        inputSchema: {
            type: 'object',
            properties: {
                taskId: { type: 'string', description: 'The task to start: a taskId from os.my_mail_actions' },
                emailId: {
                    type: 'string',
                    description: 'For a button on one message: that message’s id from os.my_mail_list',
                },
            },
            required: ['taskId'],
            additionalProperties: false,
        },
        returns: 'The run started: its `id`, `status` (`queued`, or `pending` while it waits its turn) and `taskId`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        sideEffects: 'Starts a run of the task, recorded as acting on your mailbox.',
        retry: 'unsafe',
        retryNote: 'A repeat starts a second run. Read the first run before calling again.',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            {
                code: 'task_not_found',
                when: 'taskId is not a task of this enterprise.',
                handling: 'tool_error',
            },
            {
                code: 'task_not_runnable',
                when: 'The task is deprecated, has no version yet, or nobody holds its role.',
                handling: 'tool_error',
            },
            {
                code: 'task_disabled',
                when: 'The task’s runs are switched off.',
                handling: 'tool_error',
            },
            {
                code: 'input_required',
                when: 'The task requires an input the button does not pass; the error names the field.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/mail/actions/:taskId/runs', scope: 'mail:write' },
        examples: [
            { input: { taskId: '0a7d2c9e-0000-4000-8000-000000000011' } },
            {
                input: {
                    taskId: '0a7d2c9e-0000-4000-8000-000000000012',
                    emailId: '5f0c1d2e-0000-4000-8000-000000000003',
                },
            },
        ],
        public: true,
        chatPrompt:
            'os.my_mail_start_action presses one of those buttons once, over the person’s own mailbox, when they ask you to.',
    },
    {
        name: 'os.my_mail_schedule_action',
        family: 'os.my_mail',
        summary: 'Press one of your inbox’s buttons on a cadence: each firing runs it over your own mailbox.',
        description:
            'Put one of the buttons os.my_mail_actions lists on a cadence of your own: each firing presses it for you, over your own mailbox, as os.my_mail_start_action does, and the run is recorded as asked for by you. Only a button over the whole mailbox (on mailbox) can be scheduled; one on a single message needs a message. cron is five fields (*/15 * * * * is every 15 minutes, */15 8-21 * * 1-5 the same on weekdays from 08:00 to 21:45); timezone is an IANA zone, or enterprise (the default) to follow the enterprise’s. enabled false keeps the cadence without firing it. Saving again changes it. A firing starts nothing while the button’s last run over your mailbox is still going, or failed and nobody cleared it, and says so in the schedule’s refusalReason.',
        inputSchema: {
            type: 'object',
            properties: {
                taskId: {
                    type: 'string',
                    description: 'The button to schedule: a taskId from os.my_mail_actions, on mailbox',
                },
                cron: { type: 'string', description: 'Five-field cron, read in timezone, such as */15 * * * *' },
                timezone: {
                    type: 'string',
                    description: 'An IANA zone such as Europe/Madrid, or enterprise (the default)',
                },
                enabled: { type: 'boolean', description: 'false keeps the cadence without firing it (default true)' },
            },
            required: ['taskId', 'cron'],
            additionalProperties: false,
        },
        returns:
            'The cadence as saved: `{ cron, timezone, enabled, nextRunAt, lastFiredAt, lastRunId, refusalReason }`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        sideEffects: 'Saves your cadence for the button; each firing starts a run of the task over your mailbox.',
        retry: 'idempotent',
        retryNote: 'A repeat saves the same cadence again.',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            {
                code: 'task_not_found',
                when: 'taskId is not a task of this enterprise.',
                handling: 'tool_error',
            },
            {
                code: 'not_a_mailbox_action',
                when: 'The task is not active or declares no button over the whole mailbox.',
                handling: 'tool_error',
            },
            {
                code: 'no_mail_address',
                when: 'You have no mailbox in this enterprise yet.',
                handling: 'tool_error',
            },
            {
                code: 'invalid_schedule',
                when: 'cron or timezone cannot be read, or the cadence never comes round.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'PUT', path: '/v1/mail/actions/:taskId/schedule', scope: 'mail:write' },
        examples: [
            { input: { taskId: '0a7d2c9e-0000-4000-8000-000000000011', cron: '*/15 * * * *' } },
            {
                input: {
                    taskId: '0a7d2c9e-0000-4000-8000-000000000011',
                    cron: '*/15 8-21 * * 1-5',
                    timezone: 'Europe/Madrid',
                },
            },
        ],
        public: true,
        chatPrompt:
            'os.my_mail_schedule_action puts one of those buttons on the person’s own cadence (every 15 minutes is */15 * * * *), over their own mailbox.',
    },
    {
        name: 'os.my_mail_unschedule_action',
        family: 'os.my_mail',
        summary: 'Stop pressing one of your inbox’s buttons on a cadence.',
        description:
            'Stop the cadence os.my_mail_schedule_action set for one of your inbox’s buttons. The button stays, and a run already started finishes. cleared says whether there was a cadence to stop.',
        inputSchema: {
            type: 'object',
            properties: {
                taskId: {
                    type: 'string',
                    description: 'The button whose cadence to stop: a taskId from os.my_mail_actions',
                },
            },
            required: ['taskId'],
            additionalProperties: false,
        },
        returns: '`{ cleared }`: true when a cadence was stopped, false when there was none.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        sideEffects: 'Deletes your cadence for the button.',
        retry: 'idempotent',
        retryNote: 'A repeat finds no cadence and reports cleared false.',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP],
        api: { method: 'DELETE', path: '/v1/mail/actions/:taskId/schedule', scope: 'mail:write' },
        examples: [{ input: { taskId: '0a7d2c9e-0000-4000-8000-000000000011' } }],
        public: true,
        chatPrompt: 'os.my_mail_unschedule_action stops a cadence the person set for a button.',
    },
    {
        name: 'os.my_mail_contacts',
        family: 'os.my_mail',
        summary: 'List or search your own contacts: the people you have written to, and those you added.',
        description:
            'List the contacts of your own mailbox: everyone a send, reply or forward from it went to, saved as it left, and everyone you added by hand. Most recently written to first, then by name. Pass search to keep only contacts whose name or address contains it, any case: search Sarah, or acme.com, finds the address to write to. Pass directory true to add the enterprise’s own people and members who have an address, after your contacts and each only once. limit is how many rows (default 50, max 200). Mail that arrived never makes a contact, so nobody who only wrote to you is here until you write back.',
        inputSchema: {
            type: 'object',
            properties: {
                search: {
                    type: 'string',
                    description: 'Only contacts whose name or address contains this, any case',
                },
                directory: {
                    type: 'boolean',
                    description: 'Also offer the enterprise’s own people and members who have an address',
                },
                limit: { type: 'integer', minimum: 1, maximum: 200, description: 'Rows to return. Default 50' },
            },
            additionalProperties: false,
        },
        returns: `\`{ data }\`: each contact with ${CONTACT_FIELDS}.`,
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            {
                code: 'contact_search_invalid',
                when: 'search is not text, or is longer than 200 characters.',
                handling: 'tool_error',
            },
            {
                code: 'contact_limit_invalid',
                when: 'limit is not a whole number from 1 to 200.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'GET', path: '/v1/mail/contacts', scope: 'mail:read' },
        examples: [{ input: { search: 'sarah' } }, { input: { search: 'acme', directory: true, limit: 10 } }],
        public: true,
    },
    {
        name: 'os.my_mail_contact_save',
        family: 'os.my_mail',
        summary: 'Add a contact to your own mailbox, or name one already there.',
        description:
            'Save an address to the contacts of your own mailbox. Pass address and, optionally, name. A new address is added as one you saved by hand; an address already in your contacts keeps its place and history, and takes the name when you give one. A name you set here is yours: a later send never replaces it. Leave name out to keep the name a contact already has. An empty name clears it.',
        inputSchema: {
            type: 'object',
            properties: {
                address: { type: 'string', description: 'The contact’s email address' },
                name: {
                    type: 'string',
                    description: 'The name the contact goes by. Kept over any a later send carries',
                },
            },
            required: ['address'],
            additionalProperties: false,
        },
        returns: `The contact saved, with ${CONTACT_FIELDS}.`,
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [
            NOT_A_PERSON,
            NO_MEMBERSHIP,
            {
                code: 'contact_address_invalid',
                when: 'address is not one email address.',
                handling: 'tool_error',
            },
            {
                code: 'contact_name_invalid',
                when: 'name is not text, or is longer than 200 characters.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/mail/contacts', scope: 'mail:write' },
        examples: [{ input: { address: 'sarah@acme.com', name: 'Sarah Jones' } }],
        public: true,
    },
    {
        name: 'os.my_mail_contact_delete',
        family: 'os.my_mail',
        summary: 'Remove a contact from your own mailbox.',
        description:
            'Remove one contact from your own mailbox. Pass id from os.my_mail_contacts; a directory row has none, since it is not one of your contacts. Only the contact goes: mail to and from the address stays. A later send to the address saves it again.',
        inputSchema: {
            type: 'object',
            properties: { id: { type: 'string', description: 'A contact id from os.my_mail_contacts' } },
            required: ['id'],
            additionalProperties: false,
        },
        returns: '`{ id }`: the contact that is gone.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A repeat finds no such contact and reports contact_not_found: it is gone either way.',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP, CONTACT_NOT_FOUND],
        api: { method: 'DELETE', path: '/v1/mail/contacts/:id', scope: 'mail:write' },
        examples: [{ input: { id: '7c1e2f3a-0000-4000-8000-000000000021' } }],
        public: true,
    },
    {
        name: 'os.my_mail_signature',
        family: 'os.my_mail',
        summary: 'Read the signature the OS adds to every mail you send, reply and forward.',
        description:
            'Read your own mailbox’s signature: what the OS adds after the body of every mail you send, reply or forward, from the portal, the API or MCP, unless that send says signature false. Its html is the signature as you formatted it, sanitised; its text is the plain-text form, which a mail’s text part carries under a `-- ` line. Both are empty when you have no signature. With none of your own, your mail is signed with the enterprise’s signature template, filled in with your name, title and address, unless useTemplate is false. Mail from one of your extra addresses carries that address’s own signature instead, when it has one: see os.my_mail_addresses.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns: `Your signature: ${SIGNATURE_FIELDS}.`,
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP],
        api: { method: 'GET', path: '/v1/mail/signature', scope: 'mail:read' },
        examples: [{ input: {} }],
        public: true,
    },
    {
        name: 'os.my_mail_signature_set',
        family: 'os.my_mail',
        summary: 'Set or clear the signature the OS adds to every mail you send.',
        description:
            'Set your own mailbox’s signature, which the OS adds after the body of every mail you send, reply or forward. Pass html for a formatted signature (bold, links, a few lines): it is sanitised to what mail may carry, and its plain-text form is derived from it, with each link written after its words. Or pass text alone for a plain signature. With html given, text is ignored. Each is at most 20 KB. Pass both empty to clear the signature: your mail then leaves with the enterprise’s signature template, filled in for you, or with none when there is no template or useTemplate is false. Pass useTemplate false to send with no signature at all while you have none of your own, true to use the template again; left out, it stays as it was. Given without html or text, your signature stays as it is. To sign mail from one of your extra addresses differently, use os.my_mail_address_signature_set.',
        inputSchema: {
            type: 'object',
            properties: {
                html: { type: 'string', description: 'The signature as HTML. Sanitised; its text form is derived' },
                text: { type: 'string', description: 'The signature as plain text, used when no html is given' },
                useTemplate: {
                    type: 'boolean',
                    description:
                        'Whether the enterprise’s signature template signs your mail while you have no signature of your own',
                },
            },
            additionalProperties: false,
        },
        returns: `Your signature as saved: ${SIGNATURE_FIELDS}.`,
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'Every mail you send, reply or forward from now on carries it, unless that send says signature false.',
        retry: 'idempotent',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP, ...SIGNATURE_REFUSALS],
        api: { method: 'PUT', path: '/v1/mail/signature', scope: 'mail:write' },
        examples: [
            {
                input: {
                    html: '<p><strong>Sarah Jones</strong><br>Head of Sales · <a href="https://acme.com">acme.com</a></p>',
                },
            },
            { input: { text: 'Sarah Jones\nHead of Sales' } },
            { input: { html: '', text: '' } },
            { input: { html: '', text: '', useTemplate: false } },
        ],
        public: true,
    },
    {
        name: 'os.my_mail_addresses',
        family: 'os.my_mail',
        summary: 'List your own extra addresses, each with the signature mail from it carries.',
        description:
            'List the extra addresses of your own mailbox: the further addresses at the enterprise’s domains an owner gave you, which deliver to your mailbox and which you can send from by naming one as `from` on os.my_mail_send or os.my_mail_forward. Your own address is not listed. Each comes with replyFromAlias and its own signature, which a mail leaving from that address carries in place of your mailbox’s; null means it signs as your mailbox does (os.my_mail_signature). Set or clear one with os.my_mail_address_signature_set. Adding and removing an address is an owner’s, on People.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns: `\`{ aliases }\`: each of your extra addresses, by address, with ${ADDRESS_FIELDS}. \`[]\` when you have none.`,
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP],
        api: { method: 'GET', path: '/v1/mail/aliases', scope: 'mail:read' },
        examples: [{ input: {} }],
        public: true,
    },
    {
        name: 'os.my_mail_address_signature_set',
        family: 'os.my_mail',
        summary: 'Set or clear the signature mail from one of your extra addresses carries.',
        description:
            'Set the signature of one of your own extra addresses: what the OS adds after the body of a mail you send or forward from that address, and of a reply that leaves from it, in place of your mailbox’s signature, unless that send says signature false. Pass id from os.my_mail_addresses, and html for a formatted signature: it is sanitised to what mail may carry, and its plain-text form is derived from it, with each link written after its words. Or pass text alone for a plain signature. With html given, text is ignored. Each is at most 20 KB. Pass both empty, or leave both out, to clear it: mail leaving from that address then carries your mailbox’s signature (os.my_mail_signature), or with none of your own, the enterprise’s signature template unless you switched it off, or nothing.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'An address id from os.my_mail_addresses' },
                html: { type: 'string', description: 'The signature as HTML. Sanitised; its text form is derived' },
                text: { type: 'string', description: 'The signature as plain text, used when no html is given' },
            },
            required: ['id'],
            additionalProperties: false,
        },
        returns: `\`{ aliases }\`: every one of your extra addresses after the change, as os.my_mail_addresses lists them, with ${ADDRESS_FIELDS}.`,
        callers: ['api', 'mcp'],
        effect: 'write',
        sideEffects:
            'Every mail that leaves from the address from now on carries it, unless that send says signature false.',
        retry: 'idempotent',
        errors: [NOT_A_PERSON, NO_MEMBERSHIP, ...ADDRESS_SIGNATURE_REFUSALS],
        api: { method: 'PUT', path: '/v1/mail/aliases/:id/signature', scope: 'mail:write' },
        examples: [
            {
                input: {
                    id: '3b8e4d5f-0000-4000-8000-000000000031',
                    html: '<p><strong>Acme Sales</strong><br><a href="https://acme.com">acme.com</a></p>',
                },
            },
            { input: { id: '3b8e4d5f-0000-4000-8000-000000000031', text: 'Acme Support\nWe answer within a day' } },
            { input: { id: '3b8e4d5f-0000-4000-8000-000000000031', html: '', text: '' } },
        ],
        public: true,
    },
]
